import { useEffect, useState } from "react";

import ActiveCallDetail from "./components/ActiveCallDetail";
import Button from "./components/base/Button";
import Vapi from "@vapi-ai/web";
import { isPublicKeyMissingError } from "./utils";

// Initialize Vapi with public key from environment variables
const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);

const App = () => {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);

  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);

  const { showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage } = usePublicKeyInvalid();

  // hook into Vapi events
  useEffect(() => {
    vapi.on("call-start", () => {
      setConnecting(false);
      setConnected(true);

      setShowPublicKeyInvalidMessage(false);
    });

    vapi.on("call-end", () => {
      setConnecting(false);
      setConnected(false);

      setShowPublicKeyInvalidMessage(false);
    });

    vapi.on("speech-start", () => {
      setAssistantIsSpeaking(true);
    });

    vapi.on("speech-end", () => {
      setAssistantIsSpeaking(false);
    });

    vapi.on("volume-level", (level) => {
      setVolumeLevel(level);
    });

    vapi.on("error", (error) => {
      console.error(error);

      setConnecting(false);
      if (isPublicKeyMissingError({ vapiError: error })) {
        setShowPublicKeyInvalidMessage(true);
      }
    });

    // we only want this to fire on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // call start handler
  const startCallInline = () => {
    setConnecting(true);
    vapi.start(assistantOptions);
  };
  const endCall = () => {
    vapi.stop();
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {!connected ? (
        <Button
          label={process.env.REACT_APP_ASSISTANT_NAME}
          onClick={startCallInline}
          isLoading={connecting}
        />
      ) : (
        <ActiveCallDetail
          assistantIsSpeaking={assistantIsSpeaking}
          volumeLevel={volumeLevel}
          onEndCallClick={endCall}
        />
      )}

      {showPublicKeyInvalidMessage ? <PleaseSetYourPublicKeyMessage /> : null}
      <ReturnToDocsLink />
    </div>
  );
};

const assistantOptions = {
  name: process.env.REACT_APP_ASSISTANT_NAME,
  firstMessage: process.env.REACT_APP_FIRST_MESSAGE,
  transcriber: {
    provider: "deepgram",
    model: process.env.REACT_APP_DEEPGRAM_MODEL,
    language: process.env.REACT_APP_LANGUAGE,
  },
  voice: {
    provider: "playht",
    voiceId: process.env.REACT_APP_PLAYHT_VOICE_ID,
  },
  model: {
    provider: "openai",
    model: process.env.REACT_APP_OPENAI_MODEL,
    messages: [
      {
        role: "system",
        content: `You are a customer support representative for KOL Telecom Services, a US-based Telecom Carrier (established 2008) with FCC 499, 214 licenses. We provide the following enterprise services:

1) Wholesale A2P SMS Services:
   - Global coverage with 200+ direct routes
   - 2,000+ transactions per second capacity
   - Features: Global On-net coverage, 24/7 support, two-way SMS, API connection
   - Service Levels: Direct, Premium, Wholesale, SMS to Voice (S2V), SIM

2) Wholesale Voice Services:
   - Minimum requirement: $500 payment
   - Features: Multiple protocols (SIP/H323), major codecs support
   - Routes: Direct (Gold) and Cost-effective (Silver)
   - Technical specs: High ACD, ASR, low PDD

3) ECHT Business Instant Messaging:
   - Omnichannel platform (WhatsApp, Instagram, Viber, Telegram)
   - Features: ChatCenter, contact classification, real-time statistics
   - Business automation and token management

4) Number Lookup Services:
   - HLR and MNP lookup options
   - IMSI information access
   - Mobile number portability verification
   - Roaming status checking
   - Fraud prevention capabilities

5) RCS (Rich Communication Services):
   - Rich media support (text, audio, video, buttons)
   - Features:
     * Brand verification through Google
     * Rich Cards with high-res media
     * Suggested answers and actions
     * Unlimited character support
   - Automation capabilities:
     * Purchase notifications
     * Delivery tracking
     * Two-factor authentication
     * Cart abandonment alerts
     * NPS collection

6) IP-PBX Solutions:
   - Options:
     * Hosted IP-PBX (Cloud Based)
     * On-premise IP-PBX (India and UAE)
   - Benefits:
     * Cloud-based reliability
     * Hardware preservation
     * Scalable voice channels
     * Softphone support
     * Competitive rates

7) Mail Hosting:
   - Professional email solutions
   - Features:
     * Custom domain email hosting
     * Push technology support
     * Hybrid environment capability
     * Email archiving
     * MS Office licensing
   - Security:
     * Spam protection
     * Virus protection
     * Secure business communications

Support Guidelines:
- Identify customer's service interest
- Gather business requirements
- Explain technical specifications clearly
- Provide pricing and minimum requirements
- Guide through setup process
- Create support tickets for technical issues
- Maintain professional communication

Security Protocol:
- Verify business identity
- Follow FCC compliance guidelines
- Protect sensitive information
- Direct billing inquiries appropriately

Response Guidelines:
1. Identify specific service inquiry
2. Gather business requirements
3. Explain relevant features and benefits
4. Provide technical details clearly
5. Guide through next steps
6. Offer additional service recommendations if relevant

Maintain professional, business-focused communication and emphasize our enterprise-grade solutions and global capabilities.`,
      },
    ],
  },
};

const usePublicKeyInvalid = () => {
  const [showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage] = useState(false);

  // close public key invalid message after delay
  useEffect(() => {
    if (showPublicKeyInvalidMessage) {
      setTimeout(() => {
        setShowPublicKeyInvalidMessage(false);
      }, 3000);
    }
  }, [showPublicKeyInvalidMessage]);

  return {
    showPublicKeyInvalidMessage,
    setShowPublicKeyInvalidMessage,
  };
};

const PleaseSetYourPublicKeyMessage = () => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "25px",
        left: "25px",
        padding: "10px",
        color: "#fff",
        backgroundColor: "#f03e3e",
        borderRadius: "5px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
      }}
    >
      Is your Vapi Public Key missing? (recheck your code)
    </div>
  );
};

const ReturnToDocsLink = () => {
  return (
    <a
      href="https://docs.vapi.ai"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        position: "fixed",
        top: "25px",
        right: "25px",
        padding: "5px 10px",
        color: "#fff",
        textDecoration: "none",
        borderRadius: "5px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
      }}
    >
    </a>
  );
};

export default App;
